import { useEffect, useState, useMemo } from 'react';
import { getAllDMs, sendDM, markAsRead, markAsUnread, deleteDM, deleteMultipleDMs } from '../services/dmService';
import { useAuthStore } from '../stores/authStore';
import { formatTimestamp } from '../utils/time';
import UserDetailModal from '../components/common/UserDetailModal';
import UserFinder from '../components/common/UserFinder';
import type { DirectMessage, User } from '../types';

export default function DMPage() {
  const { adminUser } = useAuthStore();
  const [dms, setDms] = useState<DirectMessage[]>([]);
  const [showUserModal, setShowUserModal] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showSend, setShowSend] = useState(false);
  const [sendTarget, setSendTarget] = useState<User | null>(null);
  const [sendContent, setSendContent] = useState('');
  const [sendError, setSendError] = useState('');
  const [detail, setDetail] = useState<DirectMessage | null>(null);

  useEffect(() => { loadDMs(); }, []);

  async function loadDMs() {
    setLoading(true);
    try { setDms(await getAllDMs()); }
    finally { setLoading(false); }
  }

  const filtered = useMemo(() => {
    if (!search) return dms;
    const kw = search.toLowerCase();
    return dms.filter(d =>
      d.senderNickname?.toLowerCase().includes(kw) ||
      d.recipientNickname?.toLowerCase().includes(kw) ||
      d.content?.toLowerCase().includes(kw)
    );
  }, [dms, search]);

  function toggleSelect(id: string) {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  }

  async function handleDeleteSelected() {
    if (!selected.size || !confirm(`${selected.size}개 쪽지를 삭제하시겠습니까?`)) return;
    await deleteMultipleDMs([...selected]);
    setSelected(new Set());
    loadDMs();
  }

  async function handleSendDM() {
    if (!sendTarget || !sendContent.trim() || !adminUser) return;
    setSendError('');
    await sendDM({
      senderId: adminUser.uid,
      senderNickname: adminUser.nickname,
      recipientId: sendTarget.uid,
      recipientNickname: sendTarget.nickname,
      content: sendContent,
    });
    setSendContent('');
    setSendTarget(null);
    setShowSend(false);
    loadDMs();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">쪽지 관리</h1>
        <div className="flex gap-2">
          <button onClick={() => setShowSend(!showSend)} className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded-lg">
            {showSend ? '닫기' : '쪽지 보내기'}
          </button>
          <button onClick={loadDMs} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">새로고침</button>
        </div>
      </div>

      {/* Send DM */}
      {showSend && (
        <div className="mb-4 p-4 bg-white rounded-xl shadow-sm border border-gray-200 space-y-3">
          <div>
            <label className="block text-sm text-gray-500 mb-1">받는 사람</label>
            <UserFinder onUserSelect={setSendTarget} labelWidth="w-16" />
          </div>
          <textarea
            value={sendContent}
            onChange={e => setSendContent(e.target.value)}
            placeholder="내용"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
          />
          {sendError && <p className="text-red-500 text-sm">{sendError}</p>}
          <button onClick={handleSendDM} disabled={!sendTarget} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400">전송</button>
        </div>
      )}

      {/* Search & Actions */}
      <div className="flex gap-3 mb-4">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="닉네임, 내용 검색..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-3 p-2 bg-blue-50 rounded-lg">
          <span className="text-sm">{selected.size}개 선택</span>
          <button onClick={handleDeleteSelected} className="text-sm text-red-600 hover:underline">삭제</button>
          <button onClick={() => setSelected(new Set())} className="text-sm text-gray-500 hover:underline">해제</button>
        </div>
      )}

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold mb-3">쪽지 상세</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex"><dt className="w-24 text-gray-500">보낸 사람</dt><dd>{detail.senderNickname}</dd></div>
              <div className="flex"><dt className="w-24 text-gray-500">받는 사람</dt><dd>{detail.recipientNickname}</dd></div>
              <div className="flex"><dt className="w-24 text-gray-500">시간</dt><dd>{formatTimestamp(detail.sentAt)}</dd></div>
              <div className="flex"><dt className="w-24 text-gray-500">읽음</dt><dd>{detail.isRead ? `O (${formatTimestamp(detail.readAt, 'short')})` : 'X'}</dd></div>
            </dl>
            <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm whitespace-pre-wrap">{detail.content}</div>
            <div className="mt-4 flex gap-2">
              {!detail.isRead && <button onClick={() => { markAsRead(detail.id); setDetail({ ...detail, isRead: true }); loadDMs(); }} className="text-sm text-blue-600">읽음 처리</button>}
              {detail.isRead && <button onClick={() => { markAsUnread(detail.id); setDetail({ ...detail, isRead: false }); loadDMs(); }} className="text-sm text-orange-600">안읽음 처리</button>}
              <button onClick={() => { deleteDM(detail.id); setDetail(null); loadDMs(); }} className="text-sm text-red-600">삭제</button>
              <button onClick={() => setDetail(null)} className="text-sm text-gray-500">닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-3 w-10"><input type="checkbox" onChange={() => { if (selected.size === filtered.length) setSelected(new Set()); else setSelected(new Set(filtered.map(d => d.id))); }} /></th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">보낸 사람</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">받는 사람</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">내용</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">읽음</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">시간</th>
              <th className="px-3 py-3 w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">로딩 중...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">쪽지 없음</td></tr>
            ) : filtered.map(dm => (
              <tr key={dm.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setDetail(dm)}>
                <td className="px-3 py-2" onClick={e => { e.stopPropagation(); toggleSelect(dm.id); }}>
                  <input type="checkbox" checked={selected.has(dm.id)} readOnly />
                </td>
                <td className="px-3 py-2"><button onClick={(e) => { e.stopPropagation(); setShowUserModal(dm.senderId); }} className="text-blue-600 hover:underline">{dm.senderNickname}</button></td>
                <td className="px-3 py-2"><button onClick={(e) => { e.stopPropagation(); setShowUserModal(dm.recipientId); }} className="text-blue-600 hover:underline">{dm.recipientNickname}</button></td>
                <td className="px-3 py-2 truncate max-w-xs">{dm.content}</td>
                <td className="px-3 py-2">{dm.isRead ? <span className="text-green-600">O</span> : <span className="text-gray-400">-</span>}</td>
                <td className="px-3 py-2 text-gray-500">{formatTimestamp(dm.sentAt, 'short')}</td>
                <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                  <button onClick={() => { deleteDM(dm.id); loadDMs(); }} className="text-xs text-red-400 hover:text-red-600">삭제</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t border-gray-200 text-sm text-gray-500">{filtered.length}건</div>
      </div>

      {showUserModal && <UserDetailModal uid={showUserModal} onClose={() => setShowUserModal(null)} />}
    </div>
  );
}
